import React, { useState } from "react";
import { Shield, Bot, Settings, BookOpen, Zap, Star, Package, Image, UserCheck } from "lucide-react";

const ServicesSection = () => {
  const [hoveredService, setHoveredService] = useState(null);

  const services = [
    {
      id: 1,
      icon: <Settings size={28} />,
      title: "Complete Telegram Setup",
      description: "Complete Telegram setup, structured and optimized for crypto communities."
    },
    {
      id: 2,
      icon: <Shield size={28} />,
      title: "Safety & Security",
      description: "Complete safety framework to maintain quality and trust within your group."
    },
    {
      id: 3,
      icon: <Bot size={28} />,
      title: "Professional Buybot Setup",
      description: "Premium Buybot setup designed with clean visuals and clear formatting."
    },
    {
      id: 4,
      icon: <Zap size={28} />,
      title: "Seamless Welcome Experience",
      description: "The best way for users to join your Telegram group with all the key information at hand for easy navigation."
    },
    {
      id: 5,
      icon: <Star size={28} />,
      title: "Additional Features",
      description: "A suite of personalized features such as custom 3D rotating emoji of your logo and bots crafted to enhance engagement and community interaction."
    },
    {
      id: 6,
      icon: <BookOpen size={28} />,
      title: "Easy-to-Follow Instructions",
      description: "Clear and practical instructions designed to help you keep your Telegram community running smoothly without the need for technical expertise."
    }
  ];

  return (
    <section id="services" className="py-16 px-4 min-h-screen flex flex-col justify-center">
      <div className="max-w-6xl mx-auto w-full">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="display-large mb-4">Our Service Package</h2>
          <p className="body-medium max-w-2xl mx-auto text-gray-400">
            Everything you need to launch and maintain a professional Telegram presence for your crypto project.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {services.map((service) => (
            <div
              key={service.id}
              className="group relative"
              onMouseEnter={() => setHoveredService(service.id)}
              onMouseLeave={() => setHoveredService(null)}
            >
              <div 
                className={`
                  relative p-6 h-full rounded-lg border-2 transition-all duration-300
                  ${hoveredService === service.id 
                    ? 'border-cyan-400 bg-gradient-to-br from-cyan-950/30 to-gray-900/80 transform -translate-y-1' 
                    : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'
                  }
                `}
              >
                {/* Icon */}
                <div 
                  className={`
                    w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-all duration-300
                    ${hoveredService === service.id 
                      ? 'bg-cyan-400/20' 
                      : 'bg-gray-800/50'
                    }
                  `}
                >
                  <div style={{ color: 'var(--brand-primary)' }}>
                    {service.icon}
                  </div>
                </div>
                
                {/* Title */}
                <h3 className={`
                  text-base font-semibold mb-2 transition-colors duration-300
                  ${hoveredService === service.id ? 'text-cyan-400' : 'text-white'}
                `}>
                  {service.title}
                </h3>
                
                {/* Description */}
                <p className="text-sm text-gray-400 leading-relaxed">
                  {service.description}
                </p>

                {/* Hover glow effect */}
                {hoveredService === service.id && (
                  <div 
                    className="absolute inset-0 rounded-lg pointer-events-none"
                    style={{
                      boxShadow: 'inset 0 0 30px rgba(0, 218, 240, 0.1)',
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pricing & Add-On Section */}
        <div className="space-y-4">
          {/* Get Complete Package Card - Similar to Add-On style */}
          <div
            className="relative rounded-lg border-2 border-cyan-400 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 218, 240, 0.05) 0%, rgba(18, 18, 18, 0.95) 100%)',
              boxShadow: '0 0 20px rgba(0, 218, 240, 0.1)'
            }}
          >
            {/* Header */}
            <div className="p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-400/20 flex items-center justify-center flex-shrink-0">
                <Package size={22} style={{ color: 'var(--brand-primary)' }} />
              </div>
              <h3 className="text-lg font-semibold text-cyan-400">
                Get Complete Package
              </h3>
            </div>
            
            {/* Content */}
            <div className="px-6 pb-6">
              {/* Divider */}
              <div 
                className="w-full h-px mb-5"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(0, 218, 240, 0.5), transparent)'
                }}
              />
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                {/* Left: Pricing Info */}
                <div className="space-y-2">
                  <p className="text-sm text-gray-300">
                    The complete package is available for <strong className="text-white">500 USDC</strong>. Contact our team directly to make payment and get started.
                  </p>
                </div>
                
                {/* Right: CTA Button */}
                <div className="lg:text-right">
                  <button 
                    className="btn-primary dark-button-animate w-full lg:w-auto"
                    style={{ padding: '12px 28px', minHeight: '48px' }}
                    onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Contact Us to Get Started
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
